import React, {useCallback, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Sentry from '@/services/sentry/Sentry';
import {
  CONTENT_SUBMISSION_ANALYTICS_BUTTONS as ANALYTICS_BUTTONS,
  CONTENT_SUBMISSION_ANALYTICS_SCREENS as ANALYTICS_SCREENS,
} from '@/lib/analytics/analyticsConstants';
import buildOffPlatformShareUrl from '@/utils/buildOffPlatformShareUrl';
import {moveContentSubmissionInCache} from '@/utils/cache-updaters/contentSubmissions';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {PHASE, type Phase} from '@/config/contentSubmissionPhases';
import {ROUTES} from '@/config/routesConstants';
import {CampaignApprovalStep} from '@/types/campaign';
import {
  ContentSubmission,
  ContentSubmissionState,
  ContentSubmissionVersion,
  CONTENT_SUBMISSION_STATUS,
} from '@/types/contentSubmission';
import ApproveContentSubmissionModal from '@/components/approve-content-submission-modal';
import OffPlatformApproveModal from '@/components/approve-content-submission-modal/OffPlatformApproveModal';
import BrandFeedbackMessageModal from '@/components/brand-feedback-message-modal';
import {
  CONTENT_SUBMISSION_ACTION,
  type ContentSubmissionAction,
} from '@/components/content-submission-card';
import useTrackButtonEvent from '@/hooks/analytics/use-track-button-event/useTrackButtonEvent';
import useTrackScreenEvent from '@/hooks/analytics/use-track-screen-event/useTrackScreenEvent';
import useContentSubmissionTransitionMutation, {
  type TransitionAction,
} from '@/hooks/mutations/use-content-submission-transition-mutation';
import useCreateOffPlatformApprovalMutation from '@/hooks/mutations/use-create-off-platform-approval';
import useStartContentSubmissionAnalysisMutation from '@/hooks/mutations/useStartContentSubmissionAnalysisMutation';
import useCampaignQuery from '@/hooks/queries/use-campaign-query';
import useContentSubmissionLink from '@/hooks/use-content-submission-link';
import useContentSubmissionOffPlatformIdentityStore from '@/stores/contentSubmissionOffPlatformIdentityStore';

type TransitionDef = {
  toState: ContentSubmissionState;
  targetPhase: Phase;
  mutationAction: TransitionAction;
};

const TRANSITION_MAP: Partial<Record<ContentSubmissionAction, TransitionDef>> =
  {
    [CONTENT_SUBMISSION_ACTION.ACCEPT]: {
      toState: CONTENT_SUBMISSION_STATUS.APPROVED,
      targetPhase: PHASE.ACCEPTED,
      mutationAction: 'accept',
    },
    [CONTENT_SUBMISSION_ACTION.ARCHIVE]: {
      toState: CONTENT_SUBMISSION_STATUS.ARCHIVED,
      targetPhase: PHASE.ARCHIVED,
      mutationAction: 'archive',
    },
    [CONTENT_SUBMISSION_ACTION.REJECT]: {
      toState: CONTENT_SUBMISSION_STATUS.REJECTED,
      targetPhase: PHASE.REJECTED,
      mutationAction: 'reject',
    },
    [CONTENT_SUBMISSION_ACTION.UNARCHIVE]: {
      toState: CONTENT_SUBMISSION_STATUS.PENDING,
      targetPhase: PHASE.PENDING,
      mutationAction: 'unarchive',
    },
  };

type ActiveAction = {
  action: ContentSubmissionAction;
  contentSubmissionId: string;
  transition: TransitionDef;
};

/**
 * Props passed to a custom action modal component.
 * Implement `onConfirm` to execute the transition and `onClose` to cancel.
 */
export type ActionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (brandMessage?: string) => void;
  contentSubmission: ContentSubmission;
  // eslint-disable-next-line react/no-unused-prop-types
  campaignId: string;
  // eslint-disable-next-line react/no-unused-prop-types
  campaignApprovalSteps: CampaignApprovalStep[];
  // eslint-disable-next-line react/no-unused-prop-types
  version: ContentSubmissionVersion;
  // eslint-disable-next-line react/no-unused-prop-types
  onDepartmentToggle?: (step: string, selected: boolean) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  onSubmit?: () => void;
};

type ActionModalComponent = React.ComponentType<ActionModalProps>;

/** Archive modal — internal note visible only to teammates. */
function DefaultArchiveModal({
  isOpen,
  onClose,
  onConfirm,
  contentSubmission: _cs,
}: ActionModalProps) {
  return (
    <BrandFeedbackMessageModal
      open={isOpen}
      onClose={onClose}
      onConfirm={(message) => onConfirm(message || undefined)}
      title={translate('Archive')}
      placeholder={translate(
        'Why would you like to archive this content?\nNotes are viewable only by your teammates. (Optional)'
      )}
      confirmLabel={translate('Archive')}
    />
  );
}

/** Reject modal — message to creator is required. */
function DefaultRejectModal({
  isOpen,
  onClose,
  onConfirm,
  contentSubmission: _cs,
}: ActionModalProps) {
  return (
    <BrandFeedbackMessageModal
      open={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title={translate('Request Edits')}
      placeholder={translate(
        'What changes would you like the creator to make? (Required)'
      )}
      confirmLabel={translate('Request Edits')}
      required
    />
  );
}

const ON_PLATFORM_ACTION_MODALS: Partial<
  Record<ContentSubmissionAction, ActionModalComponent>
> = {
  [CONTENT_SUBMISSION_ACTION.ARCHIVE]: DefaultArchiveModal,
  [CONTENT_SUBMISSION_ACTION.ACCEPT]: ApproveContentSubmissionModal,
  [CONTENT_SUBMISSION_ACTION.REJECT]: DefaultRejectModal,
};

const OFF_PLATFORM_ACTION_MODALS: Partial<
  Record<ContentSubmissionAction, ActionModalComponent>
> = {
  ...ON_PLATFORM_ACTION_MODALS,
  [CONTENT_SUBMISSION_ACTION.ACCEPT]: OffPlatformApproveModal,
};

type UseContentSubmissionActionParams = {
  campaignId: string;
  contentSubmissions: ContentSubmission[];
  isOffPlatform?: boolean;
  screenName?: string;
  onBeforeTransition?: (
    contentSubmissionId: string,
    targetPhase: Phase
  ) => Promise<void>;
  onSuccess?: (contentSubmissionId: string) => void;
};

type UseContentSubmissionActionReturn = {
  handleAction: (
    action: ContentSubmissionAction,
    contentSubmissionId: string,
    contentSubmissionVersionId?: string
  ) => Promise<void>;
  actionComponent: React.ReactNode;
};

function useContentSubmissionAction({
  campaignId,
  contentSubmissions,
  isOffPlatform = false,
  screenName,
  onBeforeTransition,
  onSuccess,
}: UseContentSubmissionActionParams): UseContentSubmissionActionReturn {
  const {pressed: pressedButton} = useTrackButtonEvent();
  const {presented, dismissed} = useTrackScreenEvent();
  const {campaign} = useCampaignQuery({campaignId, enabled: !!campaignId});
  const campaignApprovalSteps = campaign?.campaignApprovalSteps ?? [];
  const [activeAction, setActiveAction] = useState<ActiveAction | null>(null);
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const getLink = useContentSubmissionLink();
  const {mutate: submitTransitionMutation} =
    useContentSubmissionTransitionMutation();
  const {mutateAsync: startAnalysis} =
    useStartContentSubmissionAnalysisMutation();
  const {mutateAsync: createOffPlatformApproval} =
    useCreateOffPlatformApprovalMutation({
      contentSubmissionId: activeAction?.contentSubmissionId ?? '',
    });
  const userIdentity = useContentSubmissionOffPlatformIdentityStore(
    (s) => s.identities[campaignId] ?? null
  );

  const actionModals = isOffPlatform
    ? OFF_PLATFORM_ACTION_MODALS
    : ON_PLATFORM_ACTION_MODALS;

  const executeTransition = useCallback(
    async (
      contentSubmission: ContentSubmission,
      transition: TransitionDef,
      brandMessage?: string
    ) => {
      const fromState = contentSubmission.aasmState as ContentSubmissionState;
      // Unarchive routes back to the state the item was archived from. Default
      // (no archivedFromState, or archived from pending) keeps the static target.
      const unarchiveToRejected =
        transition.mutationAction === 'unarchive' &&
        contentSubmission.archivedFromState ===
          CONTENT_SUBMISSION_STATUS.REJECTED;
      const toState = unarchiveToRejected
        ? CONTENT_SUBMISSION_STATUS.REJECTED
        : transition.toState;
      const targetPhase = unarchiveToRejected
        ? PHASE.REJECTED
        : transition.targetPhase;

      await onBeforeTransition?.(contentSubmission.id, targetPhase);

      moveContentSubmissionInCache(
        campaignId,
        contentSubmission.id,
        fromState,
        toState
      );

      submitTransitionMutation(
        {
          action: transition.mutationAction,
          contentSubmissionId: contentSubmission.id,
          campaignId,
          brandMessage,
        },
        {
          onSuccess: () => onSuccess?.(contentSubmission.id),
          onError: (error) => {
            raiseFlashMessage({
              status: 'error',
              message: translate(
                error.message || 'Action failed. Please try again.'
              ),
            });
          },
        }
      );
    },
    [campaignId, onBeforeTransition, onSuccess, submitTransitionMutation]
  );

  const handleViewAiAnalysis = useCallback(
    (
      _contentSubmission: ContentSubmission,
      _contentSubmissionId: string,
      contentSubmissionVersionId?: string
    ) => {
      if (contentSubmissionVersionId) {
        setSearchParams((prev) => {
          prev.set('contentSubmissionAnalysis', contentSubmissionVersionId);
          return prev;
        });
      }
    },
    [setSearchParams]
  );

  const handleRunAnalysis = useCallback(
    async (contentSubmission: ContentSubmission) => {
      if (!contentSubmission.latestVersionId) {
        Sentry.captureMessage(
          `RUN_AI_ANALYSIS: contentSubmission ${contentSubmission.id} has no latestVersionId`
        );
        return;
      }
      await startAnalysis(contentSubmission.latestVersionId);
      raiseFlashMessage({
        status: 'success',
        message: translate('AI analysis is running in the background'),
      });
    },
    [startAnalysis]
  );

  const handleShare = useCallback(
    async (contentSubmission: ContentSubmission) => {
      if (!isOffPlatform && !campaign?.offPlatformShareToken) {
        raiseFlashMessage({
          status: 'error',
          message: translate("Share link isn't ready yet. Please try again."),
        });
        return;
      }

      try {
        await navigator.clipboard.writeText(
          buildOffPlatformShareUrl(
            campaignId,
            contentSubmission,
            campaign?.offPlatformShareToken
          )
        );
        raiseFlashMessage({
          status: 'success',
          message: translate('Share URL copied to clipboard'),
        });
      } catch {
        raiseFlashMessage({
          status: 'error',
          message: translate('Failed to copy URL'),
        });
      }
    },
    [campaignId, campaign?.offPlatformShareToken, isOffPlatform]
  );

  const handleComment = useCallback(
    (contentSubmission: ContentSubmission) => {
      const isMixedMedia = contentSubmission.kind === 'mixed_media';
      const link = isMixedMedia
        ? getLink({
            parentContentSubmissionId: contentSubmission.id,
            additionalSearchParams: {autoFocusComment: 'true'},
          })
        : getLink({
            contentSubmissionId: contentSubmission.id,
            additionalSearchParams: {autoFocusComment: 'true'},
          });
      navigate(link);
    },
    [getLink, navigate]
  );

  const handleTransition = useCallback(
    (contentSubmission: ContentSubmission, action: ContentSubmissionAction) => {
      const transition = TRANSITION_MAP[action];
      if (!transition) return;

      // Off-platform approve without identity → redirect to single asset page
      if (
        isOffPlatform &&
        action === CONTENT_SUBMISSION_ACTION.ACCEPT &&
        !userIdentity
      ) {
        const isMixedMedia = contentSubmission.kind === 'mixed_media';
        const link = isMixedMedia
          ? getLink({parentContentSubmissionId: contentSubmission.id})
          : getLink({contentSubmissionId: contentSubmission.id});
        navigate(link);
        return;
      }

      const ModalComponent = actionModals[action];
      if (ModalComponent) {
        if (screenName) {
          presented({
            screen_name: ANALYTICS_SCREENS.ASSET_ACTION_MODAL,
            source: screenName,
            tab: action,
            trigger_flow_id: activeAction?.contentSubmissionId,
          });
        }
        setActiveAction({
          action,
          contentSubmissionId: contentSubmission.id,
          transition,
        });
        return;
      }

      executeTransition(contentSubmission, transition);
    },
    [
      executeTransition,
      isOffPlatform,
      userIdentity,
      actionModals,
      getLink,
      navigate,
      presented,
      screenName,
    ]
  );

  const ACTION_HANDLERS = useMemo<
    Partial<
      Record<
        ContentSubmissionAction,
        (
          contentSubmission: ContentSubmission,
          contentSubmissionId: string,
          contentSubmissionVersionId?: string
        ) => void | Promise<void>
      >
    >
  >(
    () => ({
      [CONTENT_SUBMISSION_ACTION.VIEW_AI_ANALYSIS]: handleViewAiAnalysis,
      [CONTENT_SUBMISSION_ACTION.RUN_AI_ANALYSIS]: handleRunAnalysis,
      [CONTENT_SUBMISSION_ACTION.SHARE]: handleShare,
      [CONTENT_SUBMISSION_ACTION.COMMENT]: handleComment,
      [CONTENT_SUBMISSION_ACTION.ACCEPT]: (cs) =>
        handleTransition(cs, CONTENT_SUBMISSION_ACTION.ACCEPT),
      [CONTENT_SUBMISSION_ACTION.REJECT]: (cs) =>
        handleTransition(cs, CONTENT_SUBMISSION_ACTION.REJECT),
      [CONTENT_SUBMISSION_ACTION.ARCHIVE]: (cs) =>
        handleTransition(cs, CONTENT_SUBMISSION_ACTION.ARCHIVE),
      [CONTENT_SUBMISSION_ACTION.UNARCHIVE]: (cs) =>
        handleTransition(cs, CONTENT_SUBMISSION_ACTION.UNARCHIVE),
    }),
    [
      handleViewAiAnalysis,
      handleRunAnalysis,
      handleShare,
      handleComment,
      handleTransition,
    ]
  );

  const handleAction = useCallback(
    async (
      action: ContentSubmissionAction,
      contentSubmissionId: string,
      contentSubmissionVersionId?: string
    ) => {
      const contentSubmission = contentSubmissions.find(
        (cs) => cs.id === contentSubmissionId
      );
      if (!contentSubmission) {
        Sentry.captureMessage(
          `handleAction: contentSubmission ${contentSubmissionId} not found in contentSubmissions list`
        );
        return;
      }

      const handler = ACTION_HANDLERS[action];
      if (!handler) return;
      await handler(
        contentSubmission,
        contentSubmissionId,
        contentSubmissionVersionId
      );
    },
    [contentSubmissions, ACTION_HANDLERS]
  );

  const handleModalConfirm = useCallback(
    async (brandMessage?: string) => {
      if (!activeAction) {
        return;
      }
      const contentSubmission = contentSubmissions.find(
        (cs) => cs.id === activeAction.contentSubmissionId
      );
      if (!contentSubmission) {
        Sentry.captureMessage(
          `handleModalConfirm: contentSubmission ${activeAction.contentSubmissionId} not found`
        );
        return;
      }
      setActiveAction(null);

      if (
        isOffPlatform &&
        activeAction.action === CONTENT_SUBMISSION_ACTION.ACCEPT
      ) {
        if (!userIdentity || !contentSubmission.latestVersionId) return;
        await createOffPlatformApproval({
          contentSubmissionId: contentSubmission.id,
          contentSubmissionVersionId: contentSubmission.latestVersionId,
          step: userIdentity.role.toLowerCase(),
          email: userIdentity.email,
        });
        onSuccess?.(contentSubmission.id);
      } else {
        await executeTransition(
          contentSubmission,
          activeAction.transition,
          brandMessage
        );
      }
    },
    [
      activeAction,
      contentSubmissions,
      executeTransition,
      isOffPlatform,
      userIdentity,
      createOffPlatformApproval,
      onSuccess,
    ]
  );

  const handleModalClose = useCallback(() => {
    if (screenName && activeAction) {
      dismissed({
        screen_name: ANALYTICS_SCREENS.ASSET_ACTION_MODAL,
        destination: screenName,
        tab: activeAction.action,
        flow_id: activeAction.contentSubmissionId,
      });
    }
    setActiveAction(null);
  }, [screenName, dismissed, activeAction]);

  const ActiveModalComponent = activeAction
    ? actionModals[activeAction.action]
    : null;

  const activeContentSubmission = activeAction
    ? contentSubmissions.find(
        (cs) => cs.id === activeAction.contentSubmissionId
      )
    : null;

  // Every ContentSubmission always has at least one version (created on after_create).
  // versions are always included in the fetch via DEFAULT_CONTENT_SUBMISSION_INCLUDE.
  // However some contexts (e.g. campaign grid) may fetch without versions — guard below.
  const activeLatestVersion =
    activeContentSubmission?.versions?.find(
      (version) => version.id === activeContentSubmission.latestVersionId
    ) ?? activeContentSubmission?.versions?.[0];

  // The approve modal requires a resolved version. Other modals (archive, reject) do not.
  const isApproveAction =
    activeAction?.action === CONTENT_SUBMISSION_ACTION.ACCEPT;
  const canRenderModal =
    activeAction &&
    ActiveModalComponent &&
    activeContentSubmission &&
    (!isApproveAction || activeLatestVersion != null);

  const handleApproveSubmit = useCallback(() => {
    if (!screenName || !activeAction) return;
    pressedButton({
      button_name: ANALYTICS_BUTTONS.APPROVAL_MODAL_CONFIRM,
      screen_name: screenName,
      campaign_id: campaignId,
      creator_id: activeContentSubmission?.profile?.id,
      flow_id: activeAction.contentSubmissionId,
      tab: activeAction.action,
    });
  }, [
    screenName,
    campaignId,
    activeAction,
    activeContentSubmission,
    pressedButton,
  ]);

  const handleDepartmentToggle = useCallback(
    (step: string, selected: boolean) => {
      if (!screenName) return;
      pressedButton({
        button_name: selected
          ? ANALYTICS_BUTTONS.APPROVAL_MODAL_DEPARTMENT_SELECT
          : ANALYTICS_BUTTONS.APPROVAL_MODAL_DEPARTMENT_DESELECT,
        screen_name: screenName,
        tab: step,
      });
    },
    [screenName, pressedButton]
  );

  const actionComponent = canRenderModal ? (
    <ActiveModalComponent
      isOpen
      onClose={handleModalClose}
      onConfirm={handleModalConfirm}
      contentSubmission={activeContentSubmission}
      campaignId={campaignId}
      campaignApprovalSteps={campaignApprovalSteps}
      version={activeLatestVersion as ContentSubmissionVersion}
      onDepartmentToggle={handleDepartmentToggle}
      onSubmit={handleApproveSubmit}
    />
  ) : null;

  return {handleAction, actionComponent};
}

export default useContentSubmissionAction;
